import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useScrollLock } from "../../hooks/useScrollLock";

/**
 * Global error dialog for Baakas themes and related critical errors.
 * It looks for window.__FIREBASE_CONFIG_ERROR__ and window.__BAAKAS_GLOBAL_ERRORS__.
 */
const BaakasErrorDialog: React.FC = () => {
  const [messages, setMessages] = useState<string[]>([]);
  const [visible, setVisible] = useState(false);

  // Lock scroll while dialog is visible
  useScrollLock(visible);

  useEffect(() => {
    const gather = () => {
      const found: string[] = [];
      try {
        const fb = (window as any).__FIREBASE_CONFIG_ERROR__;
        if (fb && typeof fb === "string") found.push(fb);

        const globalErrors = (window as any).__BAAKAS_GLOBAL_ERRORS__;
        if (Array.isArray(globalErrors)) {
          globalErrors.forEach((e) => {
            if (typeof e === "string") found.push(e);
          });
        } else if (globalErrors && typeof globalErrors === "string") {
          found.push(globalErrors);
        }
      } catch (e) {
        // ignore
      }

      if (found.length > 0) {
        setMessages(found);
        setVisible(true);
      }
    };

    gather();

    // Also expose a helper to push errors dynamically from other scripts
    (window as any).__BAAKAS_PUSH_ERROR__ = (msg: string) => {
      if (!msg) return;
      setMessages((prev) => [...prev, msg]);
      setVisible(true);
    };

    return () => {
      try {
        delete (window as any).__BAAKAS_PUSH_ERROR__;
      } catch {}
    };
  }, []);

  const handleClose = () => {
    // Clear known globals
    try {
      delete (window as any).__FIREBASE_CONFIG_ERROR__;
    } catch {}
    try {
      (window as any).__BAAKAS_GLOBAL_ERRORS__ = [];
    } catch {}

    setVisible(false);
    setMessages([]);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      <div className="relative bg-white rounded-lg shadow-lg max-w-xl w-full p-5 mx-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Baakas Themes — Error</h3>
            <p className="text-sm text-slate-500 mt-1">A critical configuration error was detected.</p>
          </div>
          <button
            aria-label="Close error dialog"
            onClick={handleClose}
            className="p-2 rounded hover:bg-slate-100 text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-sm text-slate-700">
          {messages.map((m, idx) => (
            <div key={idx} className="rounded border border-slate-100 bg-slate-50 p-3">
              {m}
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default BaakasErrorDialog;
